import api from './api'

export default {
  /**
   * Obtém estatísticas gerais do dashboard
   * @param {Object} filtros - Filtros para aplicar
   * @param {boolean} aplicarRegraRetry - Se deve aplicar a regra de ignorar falhas com RETRY
   * @returns {Promise} - Resposta da API
   */
  async getEstatisticas(filtros = {}, aplicarRegraRetry = true) {
    try {
      const params = new URLSearchParams();
      
      if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio);
      if (filtros.dataFim) params.append('dataFim', filtros.dataFim);
      if (filtros.sistema) params.append('sistema', filtros.sistema);
      if (filtros.hostid) params.append('hostid', filtros.hostid);
      if (filtros.metodo) params.append('metodo', filtros.metodo);
      if (filtros.atualizado !== undefined) params.append('atualizado', filtros.atualizado);
      if (filtros.baseSizeMin !== undefined && filtros.baseSizeMin !== null) params.append('baseSizeMin', filtros.baseSizeMin);
      if (filtros.baseSizeMax !== undefined && filtros.baseSizeMax !== null) params.append('baseSizeMax', filtros.baseSizeMax);
      if (filtros.finalizado !== undefined && filtros.finalizado !== null) params.append('finalizado', filtros.finalizado.toString());
      
      // Adicionar parâmetro de controle da regra de RETRY
      params.append('aplicarRegraRetry', aplicarRegraRetry.toString());
      
      // Adicionar timestamp para evitar cache
      params.append('_t', Date.now().toString());
      
      const response = await api.get(`/dashboard/estatisticas?${params.toString()}`);
      return response;
    } catch (error) {
      console.error('Erro ao obter estatísticas:', error);
      throw error;
    }
  },

  /**
   * Obtém dados para a tabela de atualizações
   * @param {Object} filtros - Filtros para aplicar
   * @param {number} page - Página atual
   * @param {number} limit - Limite por página
   * @param {string} search - Termo de pesquisa para nome do host
   * @param {boolean} aplicarRegraRetry - Se deve aplicar a regra de ignorar falhas com RETRY
   * @param {string} sortBy - Campo para ordenação
   * @param {boolean} sortDesc - Se a ordenação é decrescente
   * @returns {Promise} - Resposta da API
   */
  async getAtualizacoes(filtros = {}, page = 1, limit = 10, search = '', aplicarRegraRetry = true, sortBy = null, sortDesc = false) {
    try {
      const params = new URLSearchParams();
      
      if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio);
      if (filtros.dataFim) params.append('dataFim', filtros.dataFim);
      if (filtros.sistema) params.append('sistema', filtros.sistema);
      if (filtros.hostid) params.append('hostid', filtros.hostid);
      if (filtros.metodo) params.append('metodo', filtros.metodo);
      if (filtros.atualizado !== undefined) params.append('atualizado', filtros.atualizado);
      if (filtros.baseSizeMin !== undefined && filtros.baseSizeMin !== null) params.append('baseSizeMin', filtros.baseSizeMin);
      if (filtros.baseSizeMax !== undefined && filtros.baseSizeMax !== null) params.append('baseSizeMax', filtros.baseSizeMax);
      if (filtros.finalizado !== undefined && filtros.finalizado !== null) params.append('finalizado', filtros.finalizado.toString());
      
      params.append('page', page);
      params.append('limit', limit);
      
      // Adicionar parâmetro de controle da regra de RETRY
      params.append('aplicarRegraRetry', aplicarRegraRetry.toString());
      
      // Adicionar parâmetro de pesquisa se fornecido
      if (search && search.trim()) {
        params.append('search', search.trim());
      }
      
      // Adicionar parâmetros de ordenação se fornecidos
      if (sortBy) {
        params.append('sortBy', sortBy);
        params.append('sortDesc', sortDesc.toString());
      }
      
      // Adicionar timestamp para evitar cache
      params.append('_t', Date.now().toString());
      
      const response = await api.get(`/dashboard/atualizacoes?${params.toString()}`);
      return response;
    } catch (error) {
      console.error('Erro ao obter atualizações:', error);
      throw error;
    }
  },

  /**
   * Obtém dados para a tabela de falhas
   * @param {Object} filtros - Filtros para aplicar
   * @param {boolean} aplicarRegraRetry - Se deve aplicar a regra de ignorar falhas com RETRY
   * @param {string} search - Termo de pesquisa para nome do host
   * @returns {Promise} - Resposta da API
   */
  async getFalhas(filtros = {}, aplicarRegraRetry = true, search = '') {
    try {
      const params = new URLSearchParams();
      
      if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio);
      if (filtros.dataFim) params.append('dataFim', filtros.dataFim);
      if (filtros.sistema) params.append('sistema', filtros.sistema);
      if (filtros.hostid) params.append('hostid', filtros.hostid);
      if (filtros.metodo) params.append('metodo', filtros.metodo);
      if (filtros.baseSizeMin !== undefined && filtros.baseSizeMin !== null) params.append('baseSizeMin', filtros.baseSizeMin);
      if (filtros.baseSizeMax !== undefined && filtros.baseSizeMax !== null) params.append('baseSizeMax', filtros.baseSizeMax);
      
      // Adicionar filtro de status (atualizado) para respeitar o filtro do frontend
      if (filtros.atualizado !== undefined && filtros.atualizado !== null) {
        params.append('atualizado', filtros.atualizado.toString());
      }
      
      // Adicionar parâmetro de controle da regra de RETRY
      params.append('aplicarRegraRetry', aplicarRegraRetry.toString());
      
      // Adicionar parâmetro de pesquisa se fornecido
      if (search && search.trim()) {
        params.append('search', search.trim());
      }
      
      // Adicionar timestamp para evitar cache
      params.append('_t', Date.now().toString());
      
      console.log('[DEBUG] getFalhas - Parâmetros enviados:', {
        filtros,
        params: params.toString()
      });
      
      const response = await api.get(`/dashboard/falhas?${params.toString()}`);
      return response;
    } catch (error) {
      console.error('Erro ao obter falhas:', error);
      throw error;
    }
  },

  /**
   * Obtém opções para filtros
   * @returns {Promise} - Resposta da API
   */
  async getOpcoesFilters() {
    try {
      // Adicionar timestamp para evitar cache
      const params = new URLSearchParams();
      params.append('_t', Date.now().toString());
      
      const response = await api.get(`/dashboard/opcoes-filtros?${params.toString()}`);
      return response;
    } catch (error) {
      console.error('Erro ao obter opções de filtros:', error);
      throw error;
    }
  },

  /**
   * Formata duração em segundos para string legível
   * @param {number} segundos - Duração em segundos
   * @returns {string} - Duração formatada
   */
  formatarDuracao(segundos) {
    console.log('🔍 DEBUG - formatarDuracao chamada com:', segundos, 'tipo:', typeof segundos);
    
    if (!segundos || segundos <= 0) return '0s';
    
    // Converter para número e arredondar para evitar problemas de precisão
    const segundosInteiros = Math.round(Number(segundos));
    console.log('🔍 DEBUG - segundosInteiros:', segundosInteiros);
    
    const horas = Math.floor(segundosInteiros / 3600);
    const minutos = Math.floor((segundosInteiros % 3600) / 60);
    const segs = segundosInteiros % 60;
    
    console.log('🔍 DEBUG - horas:', horas, 'minutos:', minutos, 'segundos:', segs);
    
    let resultado = '';
    if (horas > 0) resultado += `${horas}h `;
    if (minutos > 0) resultado += `${minutos}m `;
    if (segs > 0 || (horas === 0 && minutos === 0)) resultado += `${segs}s`;
    
    const resultadoFinal = resultado.trim();
    console.log('🔍 DEBUG - resultado final:', resultadoFinal);
    
    return resultadoFinal;
  },

  /**
   * Formata data para exibição
   * @param {string} dataString - Data em string
   * @returns {string} - Data formatada
   */
  formatarData(dataString) {
    if (!dataString) return 'N/A';
    
    try {
      const data = new Date(dataString);
      return data.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Data inválida';
    }
  },

  /**
   * Gera URL para acessar logs baseado na data filtrada
   * @param {string} hostId - ID do host
   * @param {string} sistema - Sistema (VPRA, VSUC, VSUP)
   * @param {string} dataFiltro - Data do filtro aplicado
   * @returns {string} - URL para logs
   */
  gerarUrlLogs(hostId, sistema, dataFiltro) {
    if (!hostId || !sistema || !dataFiltro) return null;
    
    // Formatar data para o padrão do sistema de logs (dd.MM.yyyy)
    const data = new Date(dataFiltro);
    const dataFormatada = data.toLocaleDateString('pt-BR').replace(/\//g, '.');
    
    return `/api/hosts/${hostId}/logs/${sistema}/${dataFormatada}`;
  },

  /**
   * Atualiza a configuração de logs de vídeo de um cronograma
   * @param {string} hostId - ID do host
   * @param {boolean} logsVideo - Se deve gravar vídeo
   * @returns {Promise} - Resposta da API
   */
  async updateVideoLogs(hostId, logsVideo) {
    try {
      const response = await api.put(`/dashboard/hosts/${hostId}/video-logs`, {
        logsVideo
      });
      return response;
    } catch (error) {
      console.error('Erro ao atualizar configuração de logs de vídeo:', error);
      throw error;
    }
  },

  /**
   * Obtém os logs de uma atualização específica
   * @param {number} idAtualizacao - ID da atualização
   * @returns {Promise} - Resposta da API
   */
  async getLogAtualizacao(idAtualizacao) {
    try {
      if (!idAtualizacao) {
        throw new Error('ID da atualização não fornecido');
      }
      
      const response = await api.get(`/dashboard/logs/${idAtualizacao}`);
      
      // Garantir que a resposta seja passada corretamente para o componente
      if (response && response.data) {
        return response.data;
      }
      
      return response;
    } catch (error) {
      console.error('Erro ao obter logs da atualização:', error);
      throw error;
    }
  },

  /**
   * Envia logs para análise com IA
   * @param {string} logs - Conteúdo dos logs
   * @returns {Promise} - Resposta contendo a análise da IA
   */
  async analisarLogIA(logs) {
    try {
      if (!logs) {
        throw new Error('Nenhum log fornecido para análise');
      }
      
      // Chamar a API de análise (através do backend)
      const response = await api.post('/ia/analisar-log', {
        logs: logs
      });
      
      // Verificar se a resposta veio no formato esperado
      if (response && response.data) {
        return response.data;
      }
      
      return {
        success: true,
        analise: response.analise
      };
    } catch (error) {
      console.error('Erro ao analisar logs com IA:', error);
      throw error;
    }
  },

  /**
   * Obtém comentário de uma atualização
   * @param {number} idAtualizacao - ID da atualização
   * @returns {Promise} - Resposta da API
   */
  async getComentario(idAtualizacao) {
    try {
      if (!idAtualizacao) {
        throw new Error('ID da atualização não fornecido');
      }
      
      const response = await api.get(`/controle_atualizacao/${idAtualizacao}?fields=comentario`);
      
      console.log('[DEBUG] Resposta bruta da API:', response);
      
      // Retornar a resposta diretamente para o modal processar
      if (response && response.data) {
        return response.data;
      }
      
      return response;
    } catch (error) {
      console.error('Erro ao obter comentário da atualização:', error);
      throw error;
    }
  },

  /**
   * Salva comentário de uma atualização
   * @param {Object} dadosComentario - Dados do comentário
   * @returns {Promise} - Resposta da API
   */
  async salvarComentario(dadosComentario) {
    try {
      if (!dadosComentario || !dadosComentario.id) {
        throw new Error('Dados do comentário inválidos');
      }
      
      const response = await api.put(`/controle_atualizacao/${dadosComentario.id}`, {
        comentario: JSON.stringify(dadosComentario)
      });
      
      if (response && response.data) {
        return response.data;
      }
      
      return response;
    } catch (error) {
      console.error('Erro ao salvar comentário:', error);
      throw error;
    }
  },

  /**
   * Obtém o range de valores (mínimo e máximo) para base_size
   * @param {string} dataInicio - Data de início no formato YYYY-MM-DD (opcional)
   * @param {string} dataFim - Data de fim no formato YYYY-MM-DD (opcional)
   * @returns {Promise} - Resposta da API contendo range { min, max }
   */
  async getBaseSizeRange(dataInicio = null, dataFim = null) {
    try {
      const params = new URLSearchParams();
      
      // Adicionar parâmetros de data se fornecidos
      if (dataInicio) {
        params.append('dataInicio', dataInicio);
      }
      if (dataFim) {
        params.append('dataFim', dataFim);
      }
      
      // Adicionar timestamp para evitar cache
      params.append('_t', Date.now().toString());
      
      console.log('🔍 DEBUG - Buscando range de base_size...', { dataInicio, dataFim });
      
      const response = await api.get(`/dashboard/base-size-range?${params.toString()}`);
      
      console.log('🔍 DEBUG - Range de base_size recebido:', response);
      
      if (response && response.data) {
        console.log('🔍 DEBUG - Range processado:', response.data);
        return response.data;
      }
      
      console.log('🔍 DEBUG - Retornando resposta direta:', response);
      return response;
    } catch (error) {
      console.error('Erro ao obter range de base_size:', error);
      throw error;
    }
  },

  /**
   * Obtém dias que contêm atualizações para exibir no calendário
   * Usa a query SQL fornecida que retorna apenas dias com atualizações
   * @param {string} inicio - Data de início no formato YYYY-MM-DD
   * @param {string} fim - Data de fim no formato YYYY-MM-DD
   * @returns {Promise} - Resposta da API contendo array de { data, atualizacao }
   */
  async getDiasComAtualizacao(inicio, fim) {
    try {
      if (!inicio || !fim) {
        throw new Error('Datas de início e fim são obrigatórias');
      }
      
      const params = new URLSearchParams();
      params.append('inicio', inicio);
      params.append('fim', fim);
      
      // Adicionar timestamp para evitar cache
      params.append('_t', Date.now().toString());
      
      console.log(`🔍 DEBUG - Buscando dias com atualizações entre ${inicio} e ${fim}`);
      
      const response = await api.get(`/dashboard/dias-com-atualizacoes?${params.toString()}`);
      
      console.log('🔍 DEBUG - Resposta completa da API:', response);
      
      if (response && response.data) {
        console.log('🔍 DEBUG - Dados retornados:', response.data);
        return response.data;
      }
      
      console.log('🔍 DEBUG - Retornando resposta direta:', response);
      return response;
    } catch (error) {
      console.error('Erro ao obter dias com atualizações:', error);
      throw error;
    }
  },

  /**
   * Obtém TODAS as atualizações sem paginação (para exportação)
   * @param {Object} filtros - Filtros para aplicar
   * @param {string} search - Termo de pesquisa para nome do host
   * @param {boolean} aplicarRegraRetry - Se deve aplicar a regra de ignorar falhas com RETRY
   * @param {string} sortBy - Campo para ordenação
   * @param {boolean} sortDesc - Se a ordenação é decrescente
   * @returns {Promise} - Resposta da API com todas as atualizações
   */
  async getAllAtualizacoes(filtros = {}, search = '', aplicarRegraRetry = true, sortBy = null, sortDesc = false) {
    try {
      const params = new URLSearchParams();
      
      if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio);
      if (filtros.dataFim) params.append('dataFim', filtros.dataFim);
      if (filtros.sistema) params.append('sistema', filtros.sistema);
      if (filtros.hostid) params.append('hostid', filtros.hostid);
      if (filtros.metodo) params.append('metodo', filtros.metodo);
      if (filtros.atualizado !== undefined) params.append('atualizado', filtros.atualizado);
      if (filtros.baseSizeMin !== undefined && filtros.baseSizeMin !== null) params.append('baseSizeMin', filtros.baseSizeMin);
      if (filtros.baseSizeMax !== undefined && filtros.baseSizeMax !== null) params.append('baseSizeMax', filtros.baseSizeMax);
      if (filtros.finalizado !== undefined && filtros.finalizado !== null) params.append('finalizado', filtros.finalizado.toString());
      
      // Limite muito alto para pegar todos os registros
      params.append('page', 1);
      params.append('limit', 999999);
      
      // Adicionar parâmetro de controle da regra de RETRY
      params.append('aplicarRegraRetry', aplicarRegraRetry.toString());
      
      // Adicionar parâmetro de pesquisa se fornecido
      if (search && search.trim()) {
        params.append('search', search.trim());
      }
      
      // Adicionar parâmetros de ordenação se fornecidos
      if (sortBy) {
        params.append('sortBy', sortBy);
        params.append('sortDesc', sortDesc.toString());
      }
      
      // Adicionar timestamp para evitar cache
      params.append('_t', Date.now().toString());
      
      const response = await api.get(`/dashboard/atualizacoes?${params.toString()}`);
      return response;
    } catch (error) {
      console.error('Erro ao obter todas as atualizações:', error);
      throw error;
    }
  },

  /**
   * Obtém TODAS as falhas sem paginação (para exportação)
   * @param {Object} filtros - Filtros para aplicar
   * @param {boolean} aplicarRegraRetry - Se deve aplicar a regra de ignorar falhas com RETRY
   * @param {string} search - Termo de pesquisa para nome do host
   * @returns {Promise} - Resposta da API com todas as falhas
   */
  async getAllFalhas(filtros = {}, aplicarRegraRetry = true, search = '') {
    try {
      const params = new URLSearchParams();
      
      if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio);
      if (filtros.dataFim) params.append('dataFim', filtros.dataFim);
      if (filtros.sistema) params.append('sistema', filtros.sistema);
      if (filtros.hostid) params.append('hostid', filtros.hostid);
      if (filtros.metodo) params.append('metodo', filtros.metodo);
      if (filtros.baseSizeMin !== undefined && filtros.baseSizeMin !== null) params.append('baseSizeMin', filtros.baseSizeMin);
      if (filtros.baseSizeMax !== undefined && filtros.baseSizeMax !== null) params.append('baseSizeMax', filtros.baseSizeMax);
      if (filtros.finalizado !== undefined && filtros.finalizado !== null) params.append('finalizado', filtros.finalizado.toString());
      
      // Adicionar filtro de status (atualizado) para respeitar o filtro do frontend
      if (filtros.atualizado !== undefined && filtros.atualizado !== null) {
        params.append('atualizado', filtros.atualizado.toString());
      }
      
      // Adicionar parâmetro de controle da regra de RETRY
      params.append('aplicarRegraRetry', aplicarRegraRetry.toString());
      
      // Adicionar parâmetro de pesquisa se fornecido
      if (search && search.trim()) {
        params.append('search', search.trim());
      }
      
      // Adicionar timestamp para evitar cache
      params.append('_t', Date.now().toString());
      
      const response = await api.get(`/dashboard/falhas?${params.toString()}`);
      return response;
    } catch (error) {
      console.error('Erro ao obter todas as falhas:', error);
      throw error;
    }
  }
}